/**
 * TokenPackPurchase — VP-256. Dokupienie tokenów wprost z panelu płatności.
 *
 * Domyślnie płatność jednorazowa (przekierowanie do Stripe Checkout). Klient
 * z aktywną subskrypcją może zamiast tego doliczyć pakiet do najbliższego
 * rachunku — wtedy tokeny są od razu, a obciążenie schodzi przy rozliczeniu.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Coins, Loader2 } from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

type TokenPack = { id: string; name: string; tokens: number; priceNet: number };

type TokenPacksResponse = { packs: TokenPack[]; canChargeToSubscription: boolean };

type PurchaseResponse =
  | { mode: "checkout"; url: string }
  | { mode: "invoiced"; tokensGranted: number; tokensRemaining: number };

/**
 * Saldo aktualizuje webhook Stripe, który po powrocie klienta bywa jeszcze w drodze.
 * Odświeżamy kilka razy, żeby licznik nie pokazywał starej wartości i klient nie
 * uznał, że płatność nie przeszła (i nie kupił drugi raz).
 */
const BALANCE_REFRESH_DELAYS_MS = [0, 3_000, 8_000];

export default function TokenPackPurchase() {
  const { t } = useTranslation("billingPanel");
  const { toast } = useToast();
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [chargeToSubscription, setChargeToSubscription] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data } = useQuery<TokenPacksResponse>({
    queryKey: ["/api/billing/token-packs"],
    queryFn: () => apiRequest("GET", "/api/billing/token-packs"),
  });

  const packs = data?.packs ?? [];
  const canChargeToSubscription = data?.canChargeToSubscription ?? false;
  const selectedPack = packs.find((p) => p.id === selectedPackId) ?? null;

  // VP-256: powrót ze Stripe. Bez tego klient wracał na pustą stronę płatności —
  // żadnego potwierdzenia, a licznik tokenów jeszcze sprzed doładowania.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get("tokens");
    if (outcome !== "success" && outcome !== "cancelled") return;

    params.delete("tokens");
    const query = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);

    if (outcome === "cancelled") {
      toast({ title: t("tokenPacks.cancelledTitle") });
      return;
    }

    toast({
      title: t("tokenPacks.successTitle"),
      description: t("tokenPacks.successDescription"),
    });
    const timers = BALANCE_REFRESH_DELAYS_MS.map((delay) =>
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] }), delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [toast, t]);

  const purchaseMutation = useMutation({
    mutationFn: (): Promise<PurchaseResponse> =>
      apiRequest("POST", "/api/billing/token-packs/purchase", {
        packId: selectedPackId,
        chargeToSubscription,
      }),
    onSuccess: (result) => {
      setConfirmOpen(false);
      if (result.mode === "checkout") {
        window.location.href = result.url;
        return;
      }
      toast({
        title: t("tokenPacks.invoicedTitle"),
        description: t("tokenPacks.invoicedDescription", {
          tokens: result.tokensGranted.toLocaleString("pl-PL"),
        }),
      });
      setSelectedPackId(null);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
    onError: () => {
      setConfirmOpen(false);
      toast({ title: t("tokenPacks.errorTitle"), variant: "destructive" });
    },
  });

  if (packs.length === 0) return null;

  return (
    <div className="bg-white/[0.04] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Coins className="w-3.5 h-3.5 text-[#ef3e29]" />
        <p className="text-white/40 text-xs">{t("tokenPacks.title")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {packs.map((pack) => {
          const isSelected = pack.id === selectedPackId;
          return (
            <button
              key={pack.id}
              onClick={() => setSelectedPackId(isSelected ? null : pack.id)}
              className={`rounded-lg border p-3 text-left transition-all ${
                isSelected
                  ? "bg-[#ef3e29]/10 border-[#ef3e29]/40"
                  : "bg-white/[0.02] border-white/[0.08] hover:border-white/[0.16]"
              }`}
            >
              <span className="block text-sm font-medium text-white">
                {pack.tokens.toLocaleString("pl-PL")}
              </span>
              <span className="block text-white/30 text-[11px]">{t("tokenPacks.tokensUnit")}</span>
              <span className="block text-white/60 text-xs mt-1.5">
                {t("tokenPacks.priceNet", { price: pack.priceNet.toLocaleString("pl-PL") })}
              </span>
            </button>
          );
        })}
      </div>

      {canChargeToSubscription && (
        <label className="flex items-start gap-2 mt-3 cursor-pointer">
          <Checkbox
            checked={chargeToSubscription}
            onCheckedChange={(checked) => setChargeToSubscription(checked === true)}
            className="mt-0.5"
          />
          <span className="text-white/50 text-xs leading-relaxed">
            {t("tokenPacks.chargeToSubscription")}
          </span>
        </label>
      )}

      <button
        onClick={() => setConfirmOpen(true)}
        disabled={!selectedPack || purchaseMutation.isPending}
        className="mt-3 w-full rounded-lg bg-[#ef3e29] hover:bg-[#d63520] disabled:opacity-30 disabled:cursor-not-allowed text-white text-xs font-medium py-2 transition-colors flex items-center justify-center gap-1.5"
      >
        {purchaseMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
        {t("tokenPacks.buyButton")}
      </button>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tokenPacks.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedPack &&
                t(
                  chargeToSubscription
                    ? "tokenPacks.confirmInvoiced"
                    : "tokenPacks.confirmImmediate",
                  {
                    tokens: selectedPack.tokens.toLocaleString("pl-PL"),
                    price: selectedPack.priceNet.toLocaleString("pl-PL"),
                  }
                )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("tokenPacks.confirmCancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                purchaseMutation.mutate();
              }}
              disabled={purchaseMutation.isPending}
            >
              {t("tokenPacks.confirmAccept")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
